import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getOrganizations, getOrganization, createOrganization, updateOrganization, deleteOrganization } from '../../api';
import OrganizationCard from './OrganizationCard';
import CreateOrganizationForm from '../forms/CreateOrganizationForm';
import TeamManagement from './TeamManagement';

function OrganizationManagement({ user, organizationId, onBack, onViewTeamLeads }) { // organizationId Parameter hinzugefügt
  const navigate = useNavigate();
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  // Mobile Detection hinzugefügt
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isTablet, setIsTablet] = useState(window.innerWidth <= 1024 && window.innerWidth > 768);

  // Mobile Responsive Check hinzugefügt
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
      setIsTablet(window.innerWidth <= 1024 && window.innerWidth > 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Konstanten für Benutzerrollen
  const isAdmin = user?.isAdmin || false;
  const isOrgLeader = user?.isOrgLeader || false;
  const isTeamLeader = user?.isTeamlead || false;

  // Zurück zum Dashboard
  const handleBackToDashboard = () => {
    if (onBack) onBack();
  };

  // Zurück zur Organisationsübersicht (nur für Admin/OrgLeiter)
  const handleBackToList = () => {
    setSelectedOrg(null);
  };

  // Sub-Team functionality removed

  // Organisationen laden
  const loadOrganizations = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await getOrganizations(user.id);
      
      if (response.success) {
        setOrganizations(response.organizations);
        
        // Falls eine spezifische organizationId übergeben wurde, diese direkt auswählen
        if (organizationId) {
          const targetOrg = response.organizations.find(org => org.id.toString() === organizationId.toString());
          if (targetOrg) {
            handleSelectOrganization(targetOrg.id);
            return;
          }
        }
        
        // Falls der Benutzer nur zu einer Organisation gehört, diese direkt auswählen
        if (response.organizations.length === 1 && !isAdmin) {
          handleSelectOrganization(response.organizations[0].id);
        }
      } else {
        setError(response.error || 'Fehler beim Laden der Organisationen');
      }
    } catch (err) {
      setError('Netzwerkfehler: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Eine Organisation auswählen
  const handleSelectOrganization = async (orgId) => {
    setLoading(true);
    setError('');

    try {
      const response = await getOrganization(user.id, orgId);
      
      if (response.success) {
        setSelectedOrg(response);
      } else {
        setError(response.error || 'Fehler beim Laden der Organisationsdetails');
      }
    } catch (err) {
      setError('Netzwerkfehler: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Eine neue Organisation erstellen
  const handleCreateOrganization = async (orgData) => {
    setLoading(true);
    setError('');

    try {
      const response = await createOrganization(user.id, orgData.name, orgData.description);
      
      if (response.success) {
        setShowCreateForm(false);
        loadOrganizations(); // Liste aktualisieren
      } else {
        setError(response.error || 'Fehler beim Erstellen der Organisation');
      }
    } catch (err) {
      setError('Netzwerkfehler: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Eine Organisation aktualisieren
  const handleUpdateOrganization = async (orgId, orgData) => {
    setLoading(true);
    setError('');

    try {
      const response = await updateOrganization(user.id, orgId, orgData.name, orgData.description);
      
      if (response.success) {
        loadOrganizations(); // Liste aktualisieren
        
        // Falls aktuell ausgewählte Organisation aktualisiert wurde, neu laden
        if (selectedOrg && selectedOrg.organization.id === orgId) {
          handleSelectOrganization(orgId);
        }
      } else {
        setError(response.error || 'Fehler beim Aktualisieren der Organisation');
      }
    } catch (err) {
      setError('Netzwerkfehler: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Eine Organisation löschen
  const handleDeleteOrganization = async (orgId) => {
    if (!window.confirm('Sind Sie sicher, dass Sie diese Organisation löschen möchten? Alle Teams und Benutzerzuordnungen werden ebenfalls entfernt.')) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await deleteOrganization(user.id, orgId);
      
      if (response.success) {
        // Falls aktuell ausgewählte Organisation gelöscht wurde, zurück zur Liste
        if (selectedOrg && selectedOrg.organization.id === orgId) {
          setSelectedOrg(null);
        }
        
        loadOrganizations(); // Liste aktualisieren
      } else {
        setError(response.error || 'Fehler beim Löschen der Organisation');
      }
    } catch (err) {
      setError('Netzwerkfehler: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Beim ersten Laden Organisationen abrufen
  useEffect(() => {
    loadOrganizations();
  }, [organizationId]); // Abhängigkeit von organizationId hinzugefügt

  // Render-Funktion für die Organisationsübersicht
  const renderOrganizationsList = () => {
    if (loading) {
      return <div className="loading">Organisationen werden geladen...</div>;
    }

    if (error) {
      return <div className="error-message">{error}</div>;
    }

    if (organizations.length === 0) {
      return (
        <div className="no-data">
          Keine Organisationen gefunden.
          {isAdmin && (
            <button 
              className="add-org-button" 
              onClick={() => setShowCreateForm(true)}
            >
              Organisation erstellen
            </button>
          )}
        </div>
      );
    }

    return (
      <div className="organizations-grid">
        {organizations.map(org => (
          <OrganizationCard
            key={org.id}
            organization={org}
            onSelect={() => handleSelectOrganization(org.id)}
            onUpdate={(isAdmin || (isOrgLeader && user.organization_id === org.id)) ? 
              (data) => handleUpdateOrganization(org.id, data) : null}
            onDelete={isAdmin ? () => handleDeleteOrganization(org.id) : null}
          />
        ))}
        
        {isAdmin && (
          <div className="add-org-card" onClick={() => setShowCreateForm(true)}>
            <div className="add-icon">+</div>
            <div>Organisation hinzufügen</div>
          </div>
        )}
      </div>
    );
  };

  // Wenn Teamleiter, direkt zur Team-Verwaltung navigieren
  useEffect(() => {
    if (isTeamLeader && user.team && user.team.id) {
      // Team-ID des Benutzers ermitteln
      const teamId = user.team.id;
      
      // Organisation-ID für dieses Team ermitteln
      // Hinweis: Dies erfordert möglicherweise eine separate API
      // Für das Beispiel nehmen wir an, dass im user-Objekt die org_id vorhanden ist
      const orgId = user.organization_id;
      
      if (orgId) {
        handleSelectOrganization(orgId);
      }
    }
  }, [isTeamLeader, user]);
  
  // Wenn Organisationsleiter, direkt zur eigenen Organisation navigieren
  useEffect(() => {
    if (isOrgLeader && user.organization_id) {
      handleSelectOrganization(user.organization_id);
    }
  }, [isOrgLeader, user]);

  // Hauptkomponente rendern
  return (
    <div className="organization-management">
      <div className="page-header">
        <h2>Organisationen</h2>
      </div>
      
      {error && <div className="error-message">{error}</div>}
      
      {showCreateForm ? (
        <CreateOrganizationForm
          onSubmit={handleCreateOrganization}
          onCancel={() => setShowCreateForm(false)}
          loading={loading}
        />
      ) : selectedOrg ? (
        <TeamManagement 
          organization={selectedOrg.organization}
          teams={selectedOrg.teams}
          defaultTeamId={selectedOrg.defaultTeamId}
          user={user}
          onBack={handleBackToDashboard} // Hier immer zum Dashboard zurück
          onViewTeamLeads={onViewTeamLeads} // Parameter an TeamManagement weitergeben
          // Mobile Props hinzugefügt
          isMobile={isMobile}
          isTablet={isTablet}
        />
      ) : (
        renderOrganizationsList()
      )}
      
      {/* Mobile-spezifische Styles nur für Tabellen hinzugefügt */}
      <style>{`
        .organization-management .teams-table-container,
        .organization-management .users-table-container {
          overflow: auto;
          -webkit-overflow-scrolling: touch;
          max-height: 60vh;
          background: white;
          border-radius: 6px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }
        
        .organization-management .teams-table,
        .organization-management .users-table {
          min-width: ${isMobile ? '700px' : isTablet ? '800px' : 'auto'};
          font-size: ${isMobile ? '0.7rem' : isTablet ? '0.75rem' : '0.85rem'};
        }
        
        .organization-management .teams-table th,
        .organization-management .teams-table td,
        .organization-management .users-table th,
        .organization-management .users-table td {
          padding: ${isMobile ? '6px 4px' : isTablet ? '8px 6px' : '10px 8px'};
          font-size: ${isMobile ? '0.65rem' : isTablet ? '0.7rem' : '0.75rem'};
          max-width: ${isMobile ? '80px' : isTablet ? '100px' : '150px'};
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        
        .organization-management .teams-table th,
        .organization-management .users-table th {
          position: sticky;
          top: 0;
          z-index: 10;
          background: #f7fafc;
        }
        
        .organization-management .mobile-scroll-hint {
          display: ${isMobile || isTablet ? 'block' : 'none'};
          font-size: 0.65rem;
          color: #718096;
          text-align: center;
          margin-top: 8px;
          font-style: italic;
          padding: 5px;
        }
        
        .organization-management .action-button-mobile {
          padding: ${isMobile ? '3px 6px' : '4px 8px'};
          font-size: ${isMobile ? '0.6rem' : '0.7rem'};
          margin: 1px;
        }
        
        .organization-management .role-badge-mobile {
          padding: ${isMobile ? '0.15rem 0.3rem' : '0.2rem 0.5rem'};
          font-size: ${isMobile ? '0.55rem' : '0.65rem'};
          min-width: ${isMobile ? '35px' : '50px'};
          max-width: ${isMobile ? '70px' : '90px'};
          overflow: hidden;
          text-overflow: ellipsis;
        }
      `}</style>
    </div>
  );
}

export default OrganizationManagement;